import { ArrowRight, MapPin, Mail } from "lucide-react";
import { heroCardPicture, heroPicture } from "@/lib/images";
import { ResponsivePicture } from "./ResponsivePicture";

export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
      {/* Background image — LCP candidate; keep visible on first paint. */}
      <div className="absolute inset-0 -z-10">
        <ResponsivePicture
          {...heroPicture}
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover opacity-[0.35]"
        />
        <div
          className="absolute inset-0"
          style={{ background: "var(--gradient-hero)" }}
        />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-[1.15fr_1fr] lg:items-center">
        <div>
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
            20+ Years Serving Collectors
          </p>

          {/* LCP text: keep this heading in the first HTML paint (no Reveal, no opacity:0). */}
          <h1 className="font-display text-5xl leading-[1.02] text-primary md:text-7xl">
            Postage stamps
            <br />
            with a{" "}
            <span className="italic text-accent">story</span> behind
            <br />
            every perforation.
          </h1>

          <p className="mt-6 max-w-xl text-lg text-foreground">
            A curated collection of United States and worldwide stamps — early
            issues, souvenir sheets, first day covers, plate blocks and more —
            from a family-run shop in the North Carolina mountains.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="https://www.ebay.com/str/carolinamountainstamps"
              target="_blank"
              rel="noreferrer noopener"
              className="group inline-flex min-h-12 items-center gap-2 rounded-sm bg-accent px-5 py-3 text-sm font-medium text-accent-foreground shadow-[var(--shadow-stamp)] transition-transform hover:-translate-y-0.5"
            >
              Browse the eBay Store
              <span className="sr-only">(opens in a new tab)</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <a
              href="#locations"
              className="inline-flex min-h-12 items-center gap-2 rounded-sm border border-border bg-card/70 px-5 py-3 text-sm font-medium text-foreground backdrop-blur hover:bg-card"
            >
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Visit the shop
            </a>
          </div>

          <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-border/70 pt-6">
            {[
              { k: "20+", v: "Years selling" },
              { k: "1", v: "NC location" },
              { k: "10¢", v: "Pick-a-packet stamps" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-2xl text-primary">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stacked stamp card — decorative, desktop only, not LCP. */}
        <div className="relative hidden lg:block">
          <div className="absolute -inset-6 -z-10 rounded-lg bg-primary/5 blur-2xl" />
          <div className="relative mx-auto max-w-md">
            <div
              className="stamp-float relative rotate-[-4deg] bg-card p-3 shadow-[var(--shadow-elegant)]"
            >
              <div className="border-2 border-dashed border-border p-4">
                <ResponsivePicture
                  {...heroCardPicture}
                  alt=""
                  className="aspect-[4/5] w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
                <div className="mt-3 flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
                  <span>Carolina Mountain</span>
                  <span>Est. Hendersonville, NC</span>
                </div>
              </div>
            </div>
            <div className="absolute -right-6 -bottom-8 hidden rotate-6 border border-border bg-card px-5 py-4 shadow-[var(--shadow-stamp)] md:flex md:items-center md:gap-3">
              <Mail className="h-4 w-4 text-accent" aria-hidden="true" />
              <div className="text-xs">
                <div className="font-medium text-foreground">Fast email replies</div>
                <div className="text-muted-foreground">Want lists welcome</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
